/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import { useTranslation } from "react-i18next";
import { useUserContext } from "context/UserContext";
import { getMdmAuthKeyByEmail } from "utils/getMdmAuthKeyByEmail";

const CRM_API_BASE = "https://crm-mqtt-718408119422.asia-east1.run.app";

const asArray = (value) => (Array.isArray(value) ? value : []);

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatValue(value);
  return date.toLocaleString("zh-TW", { hour12: false });
};

const primaryActionButtonSx = {
  minWidth: 96,
  fontSize: "0.8rem",
  fontWeight: 700,
  letterSpacing: "0.01em",
  color: "#fff",
  backgroundColor: "#1a73e8",
  borderColor: "#1a73e8",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#1557b0",
    borderColor: "#1557b0",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    color: "#fff",
    backgroundColor: "#9bbcf3",
    borderColor: "#9bbcf3",
  },
};

const secondaryActionButtonSx = {
  minWidth: 96,
  fontSize: "0.8rem",
  fontWeight: 700,
  letterSpacing: "0.01em",
  color: "#1a73e8",
  borderColor: "#1a73e8",
  backgroundColor: "#eef4ff",
  boxShadow: "none",
  "&:hover": {
    color: "#1557b0",
    borderColor: "#1557b0",
    backgroundColor: "#dde9ff",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    color: "#8aa3c7",
    borderColor: "#c7d6ee",
    backgroundColor: "#f5f8fc",
  },
};

const sanitizeDataTableValue = (value) => {
  if (React.isValidElement(value)) return value;
  if (Array.isArray(value)) return JSON.stringify(value);
  if (value && typeof value === "object") return JSON.stringify(value);
  return value;
};

const sanitizeDataTableRow = (row) =>
  Object.fromEntries(
    Object.entries(row || {}).map(([key, value]) => [key, sanitizeDataTableValue(value)])
  );

function MembershipManagementBK() {
  const { t } = useTranslation("common");
  const { userData } = useUserContext() || {};
  const email = userData?.email ? userData.email.trim().toLowerCase() : "";
  const mdmAuthKey = useMemo(() => getMdmAuthKeyByEmail(email), [email]);

  const [filters, setFilters] = useState({
    start: "",
    end: "",
    showDetail: true,
  });
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState("");
  const [memberDetail, setMemberDetail] = useState(null);
  const [tierDetail, setTierDetail] = useState(null);

  const fetchMembers = useCallback(async () => {
    setMembersLoading(true);
    setMembersError("");

    try {
      const response = await fetch(`${CRM_API_BASE}/api/v1/member/list`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Mdm-Auth": mdmAuthKey,
        },
        body: JSON.stringify({
          start: filters.start,
          end: filters.end,
          page: 1,
          size: 1000,
          showDetail: filters.showDetail,
        }),
      });

      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || `Request failed (${response.status})`);
      }

      setMembers(asArray(json?.body?.data));
    } catch (error) {
      console.error(error);
      setMembers([]);
      setMembersError(error.message || "會員資料載入失敗");
    } finally {
      setMembersLoading(false);
    }
  }, [filters.end, filters.showDetail, filters.start, mdmAuthKey]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const memberTable = useMemo(() => {
    const columns = [
      { Header: "狀態", accessor: "status" },
      { Header: "會員 ID", accessor: "id" },
      { Header: "會員名稱", accessor: "name" },
      { Header: "電話", accessor: "phone" },
      { Header: "累積點數", accessor: "points" },
      { Header: "會員等級", accessor: "tierName" },
      { Header: "等級明細", accessor: "tierAction", align: "center", disableSortBy: true },
      { Header: "會員資料", accessor: "memberAction", align: "center", disableSortBy: true },
    ];

    const rows = members.map((row, index) =>
      sanitizeDataTableRow({
        status: row?.status ?? "",
        id: row?.id ?? `${index}`,
        name: row?.name ?? "",
        phone: row?.phone ?? "",
        points: row?.points ?? "",
        tierName: row?.tier?.name ?? formatValue(row?.tier),
        tierActionValue: row?.tier?.name ?? "",
        memberActionValue: row?.id ?? "",
        tierAction: (
          <Button
            size="small"
            variant="outlined"
            sx={secondaryActionButtonSx}
            onClick={() => setTierDetail(row?.tier || null)}
            disabled={!row?.tier}
          >
            詳細資料
          </Button>
        ),
        memberAction: (
          <Button
            size="small"
            variant="contained"
            sx={primaryActionButtonSx}
            onClick={() => setMemberDetail(row)}
          >
            會員資料
          </Button>
        ),
      })
    );

    return { columns, rows };
  }, [members]);

  const memberDetailTable = useMemo(() => {
    if (!memberDetail) return { columns: [], rows: [] };

    const columns = [
      { Header: "欄位", accessor: "label" },
      { Header: "內容", accessor: "value" },
    ];

    const rows = [
      ["會員 ID", memberDetail.id],
      ["會員名稱", memberDetail.name],
      ["暱稱", memberDetail.nickName],
      ["狀態", memberDetail.status],
      ["電話", memberDetail.phone],
      ["Email", memberDetail.email],
      ["累積點數", memberDetail.points],
      ["生日", formatDate(memberDetail.birthday)],
      ["載具代碼", memberDetail.carrierCode],
      ["性別", memberDetail.gender],
      ["身份證或統編", memberDetail.identity],
      ["推薦碼", memberDetail.referralCode],
      ["地址", memberDetail.address],
      ["城市", memberDetail.city],
      ["區域", memberDetail.district],
    ].map(([label, value], index) =>
      sanitizeDataTableRow({
        id: `${label}-${index}`,
        label,
        value: formatValue(value),
      })
    );

    return { columns, rows };
  }, [memberDetail]);

  const tierDetailTable = useMemo(() => {
    if (!tierDetail) return { columns: [], rows: [] };

    return {
      columns: [
        { Header: "會員等級名稱", accessor: "name" },
        { Header: "會員等級順序", accessor: "seq" },
        { Header: "升級此等級所需最小門檻", accessor: "minThreshold" },
        { Header: "每單位點數兌換金額", accessor: "costPerUnit" },
      ],
      rows: [
        sanitizeDataTableRow({
          id: tierDetail?.name || "tier-detail",
          name: tierDetail?.name ?? "",
          seq: tierDetail?.seq ?? "",
          minThreshold: tierDetail?.minThreshold ?? "",
          costPerUnit: tierDetail?.costPerUnit ?? "",
        }),
      ],
    };
  }, [tierDetail]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  {t("pageTitles.membershipManagement")}
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                <Stack spacing={2}>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <TextField
                      label="開始時間"
                      type="date"
                      size="small"
                      value={filters.start}
                      onChange={(event) =>
                        setFilters((prev) => ({ ...prev, start: event.target.value }))
                      }
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                    <TextField
                      label="結束時間"
                      type="date"
                      size="small"
                      value={filters.end}
                      onChange={(event) =>
                        setFilters((prev) => ({ ...prev, end: event.target.value }))
                      }
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={filters.showDetail}
                          onChange={(event) =>
                            setFilters((prev) => ({ ...prev, showDetail: event.target.checked }))
                          }
                        />
                      }
                      label="是否顯示明細"
                    />
                    <Button
                      variant="contained"
                      onClick={fetchMembers}
                      disabled={membersLoading}
                      sx={{ ...primaryActionButtonSx, minWidth: 140 }}
                    >
                      {membersLoading ? "查詢中..." : "查詢會員"}
                    </Button>
                  </Stack>

                  {membersError ? <Alert severity="error">{membersError}</Alert> : null}

                  <MDTypography variant="h6">會員清單</MDTypography>
                  {membersLoading ? (
                    <MDBox display="flex" justifyContent="center" py={4}>
                      <CircularProgress />
                    </MDBox>
                  ) : (
                    <DataTable
                      table={memberTable}
                      entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
                      canSearch
                      showTotalEntries={false}
                      isSorted
                      noEndBorder
                    />
                  )}
                </Stack>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={Boolean(memberDetail)}
        onClose={() => setMemberDetail(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>會員資料</DialogTitle>
        <DialogContent>
          {memberDetailTable.rows.length > 0 ? (
            <DataTable
              table={memberDetailTable}
              entriesPerPage={false}
              canSearch={false}
              showTotalEntries={false}
              isSorted={false}
              noEndBorder
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(tierDetail)}
        onClose={() => setTierDetail(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>會員等級資料</DialogTitle>
        <DialogContent>
          {tierDetail ? (
            <DataTable
              table={tierDetailTable}
              entriesPerPage={false}
              canSearch={false}
              showTotalEntries={false}
              isSorted={false}
              noEndBorder
            />
          ) : (
            <Alert severity="info">此會員沒有等級資料</Alert>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default MembershipManagementBK;
